// apps/client/src/pages/depreciation/Depreciation.tsx
import {
  AssetActivityAllocation,
  ProjectAsset,
  ResearchActivity,
  ResearchActivityType,
  UserRole,
} from '@avanzu/types';
import {
  DevicesOutlined,
  EditOutlined,
  RequestQuoteOutlined,
  ScienceOutlined,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { EmptyStateContent } from '../../components/EmptyStateContent';
import { LoadingComponent } from '../../components/LoadingComponent';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import ContentWrapper from '../../layout/ContentWrapper';
import { apiClient } from '../../services';
import { fetchProjectActivities } from '../../services/projects';
import formatCurrency from '../../utils/formatCurrency';
import XeroSyncAssets from './components/XeroSyncAssets';

export const DepreciationPageRoles = [
  UserRole.AVANZU,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
];

interface AssetDepreciationData {
  id: string;
  assetName: string;
  assetNumber?: string;
  serialNumber?: string;
  depreciationAmount: number;
  projectAllocation: number;
  allocations: {
    activityId: string;
    amount: number;
  }[];
  assetStatus?: string;
  accountingBookValue?: number;
}

export const Depreciation: React.FC = () => {
  const [assets, setAssets] = useState<AssetDepreciationData[]>([]);
  const [activities, setActivities] = useState<ResearchActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllAssets, setShowAllAssets] = useState(false);

  const { currentOrganisation, organisationId } = useAuth();
  const { currentProject } = useProject();
  const projectId = currentProject?.id;
  const navigate = useNavigate();

  useEffect(() => {
    if (organisationId && projectId) {
      loadData();
    }
  }, [organisationId, projectId, showAllAssets]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch activities and asset data in parallel
      const [activitiesData, assetsResponse] = await Promise.all([
        fetchProjectActivities(organisationId!, projectId!),
        apiClient.get(
          `/organisations/${organisationId}/projects/${projectId}/assets${
            showAllAssets ? '?allocatedOnly=false' : '?allocatedOnly=true'
          }`
        ),
      ]);

      if (!assetsResponse.ok) {
        throw new Error('Failed to fetch asset data');
      }

      const assetsData = await assetsResponse.json();

      if (assetsData.success) {
        setActivities(activitiesData.activities);

        // Transform ProjectAsset[] to AssetDepreciationData[]
        const transformedAssets: AssetDepreciationData[] = assetsData.assets.map(
          (asset: ProjectAsset) => {
            const allocations =
              asset.allocations?.map((allocation: AssetActivityAllocation) => ({
                activityId: allocation.activityId,
                amount:
                  ((asset.depreciationAmount || 0) *
                    (asset.projectAllocation || 0) *
                    (allocation.allocation || 0)) /
                  10000, // Convert percentages to actual amounts
              })) || [];

            return {
              id: asset.id,
              assetName: asset.assetName,
              assetNumber: asset.assetNumber,
              serialNumber: asset.serialNumber,
              depreciationAmount: asset.depreciationAmount || 0, // Backend calculated value
              projectAllocation: asset.projectAllocation || 0,
              allocations,
              assetStatus: asset.assetStatus, // For debugging
              accountingBookValue: asset.accountingBookValue, // For debugging
            };
          }
        );

        setAssets(transformedAssets);
      } else {
        setError(assetsData.message || 'Failed to load asset data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Error loading depreciation data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleShowAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowAllAssets(event.target.checked);
  };

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      return a.sortOrder - b.sortOrder;
    });
  }, [activities]);

  const getAssetAllocation = (asset: AssetDepreciationData, activityId: string) => {
    return asset.allocations.find((a) => a.activityId === activityId);
  };

  const getActivityTotal = (activityId: string) => {
    return assets.reduce((total, asset) => {
      const allocation = getAssetAllocation(asset, activityId);
      return total + (allocation ? allocation.amount : 0);
    }, 0);
  };

  const getAllocatedAssets = () => {
    return assets.filter((asset) => asset.projectAllocation > 0);
  };

  const unsortedAssets = showAllAssets ? assets : getAllocatedAssets();

  const displayAssets = React.useMemo(() => {
    return [...unsortedAssets].sort((a, b) => {
      // Handle cases where assetNumber might be null or undefined
      if (!a.assetNumber && !b.assetNumber) return 0;
      if (!a.assetNumber) return 1; // Put assets without numbers at the end
      if (!b.assetNumber) return -1; // Put assets with numbers first

      // Parse as numbers if they're numeric, otherwise sort alphabetically
      const aNum = parseInt(a.assetNumber, 10);
      const bNum = parseInt(b.assetNumber, 10);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum; // Numeric sort
      }

      // Fallback to string comparison
      return a.assetNumber.localeCompare(b.assetNumber);
    });
  }, [unsortedAssets]);

  if (!organisationId || !projectId || !currentProject || !currentOrganisation) {
    return (
      <ContentWrapper>
        <EmptyStateContent
          icon={<RequestQuoteOutlined />}
          title="No project selected"
          message="Please select a project to view asset depreciation."
        />
      </ContentWrapper>
    );
  }

  if (loading) {
    return (
      <ContentWrapper>
        <PaperSection title="Asset Depreciation">
          <LoadingComponent context="Loading depreciation data..." />
        </PaperSection>
      </ContentWrapper>
    );
  }

  if (error) {
    return (
      <ContentWrapper>
        <PaperSection title="Asset Depreciation">
          <EmptyStateContent
            icon={<RequestQuoteOutlined />}
            title="Error loading depreciation data"
            message={error}
          />
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PaperSection
        title={`Asset Depreciation - ${currentProject?.name}`}
        action={
          <>
            <Button
              component={RouterLink}
              to={`/app/assets/${projectId}`}
              sx={{ color: 'primary.main' }}
            >
              Manage Asset Allocations
            </Button>
            <XeroSyncAssets onSyncComplete={() => loadData()} />
          </>
        }
      >
        {displayAssets.length === 0 ? (
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<DevicesOutlined />}
              title={showAllAssets ? 'No assets found' : 'No allocated assets found'}
              message={
                showAllAssets
                  ? 'No assets found for this project. Sync with Xero to import assets.'
                  : 'Avanzu can automatically calculate your project asset depreciation. Allocate some assets to this project to begin.'
              }
              button={
                <Button variant="contained" onClick={() => navigate(`/app/assets/${projectId}`)}>
                  Allocate your first asset
                </Button>
              }
              showBorder={false}
            />
          </Box>
        ) : activities.length === 0 ? (
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<ScienceOutlined />}
              title="No research activities"
              message="Create research activities first to view salary allocations."
              button={
                <Button
                  variant="contained"
                  onClick={() => navigate(`/app/activities/${projectId}`)}
                >
                  Create your first activity
                </Button>
              }
              showBorder={false}
            />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 0, mt: -2 }}>
              <FormControlLabel
                control={<Switch checked={showAllAssets} onChange={handleToggleShowAll} />}
                label={
                  <Typography variant="body2" color="text.secondary">
                    Show all assets
                  </Typography>
                }
              />
            </Box>

            <TableContainer
              component={Box}
              sx={{
                mx: -6,
                px: 6,
                width: 'calc(100% + 96px)',
                overflowX: 'scroll',
                '&::-webkit-scrollbar': {
                  WebkitAppearance: 'none',
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '4px',
                  backgroundColor: 'rgba(0, 0, 0, .3)',
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead
                  sx={{
                    '& .MuiTableCell-head': {
                      zIndex: (theme) => theme.zIndex.appBar + 1,
                      backgroundColor: 'background.paper',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      borderTop: '0px none',
                    },
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ width: 300, minWidth: 300, fontWeight: 600 }}>Asset</TableCell>
                    <TableCell
                      sx={{ width: 100, minWidth: 100, fontWeight: 600, textAlign: 'center' }}
                    >
                      Annual Depreciation
                    </TableCell>
                    <TableCell
                      sx={{
                        width: 120,
                        minWidth: 120,
                        fontWeight: 600,
                        textAlign: 'center',
                        backgroundColor: '#EFF2FE !important',
                      }}
                    >
                      Project %
                    </TableCell>
                    <TableCell
                      sx={{ width: 100, minWidth: 100, fontWeight: 600, textAlign: 'center' }}
                    >
                      Project Depreciation
                    </TableCell>
                    {sortedActivities.map((activity) => (
                      <TableCell
                        key={activity.id}
                        sx={{ width: 100, minWidth: 100, p: 1, verticalAlign: 'top' }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            label={activity.type === ResearchActivityType.CORE ? 'CORE' : 'SUPP'}
                            size="small"
                            color={activity.type === ResearchActivityType.CORE ? 'info' : 'primary'}
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            align="center"
                            sx={{ lineHeight: 1.2 }}
                          >
                            {activity.name}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayAssets.map((asset) => {
                    const isUnallocated = asset.projectAllocation === 0;
                    return (
                      <TableRow
                        key={asset.id}
                        sx={isUnallocated ? { opacity: 0.5, backgroundColor: 'grey.50' } : {}}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 20,
                                height: 20,
                                bgcolor: 'primary.light',
                              }}
                            >
                              <DevicesOutlined fontSize="small" sx={{ fontSize: 14 }} />
                            </Avatar>

                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" fontWeight={500}>
                                  {asset.assetName}
                                </Typography>
                                <Tooltip title="Edit asset allocation" arrow>
                                  <IconButton
                                    component={RouterLink}
                                    to={`/app/assets/${projectId}?asset=${asset.assetNumber}`}
                                    size="small"
                                    sx={{
                                      p: 0.25,
                                      color: 'text.secondary',
                                      '&:hover': { color: 'primary.main' },
                                    }}
                                  >
                                    <EditOutlined sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              {(asset.assetNumber || asset.serialNumber) && (
                                <Typography variant="caption" color="text.secondary">
                                  {asset.assetNumber && `#${asset.assetNumber}`}
                                  {asset.assetNumber && asset.serialNumber && ' • '}
                                  {asset.serialNumber && `SN: ${asset.serialNumber}`}
                                </Typography>
                              )}
                              {!isUnallocated && asset.depreciationAmount === 0 && (
                                <Typography
                                  variant="caption"
                                  color="warning.main"
                                  sx={{ display: 'block' }}
                                >
                                  No depreciation data
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" color="text.primary">
                            {formatCurrency(asset.depreciationAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'center',
                            bgcolor: '#f8f9ff',
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={isUnallocated ? 'text.disabled' : 'primary.main'}
                          >
                            {asset.projectAllocation.toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" color="text.primary">
                            {formatCurrency(
                              asset.depreciationAmount * asset.projectAllocation * 0.01
                            )}
                          </Typography>
                        </TableCell>
                        {sortedActivities.map((activity) => {
                          const allocation = getAssetAllocation(asset, activity.id);
                          return (
                            <TableCell key={activity.id} sx={{ textAlign: 'center' }}>
                              {allocation && allocation.amount > 0 ? (
                                <Typography variant="body2" fontWeight={500}>
                                  {formatCurrency(allocation.amount)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.disabled">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}

                  {/* Totals row */}
                  {displayAssets.length > 1 && (
                    <TableRow
                      sx={{
                        bgcolor: 'grey.50',
                        '& .MuiTableCell-root': {
                          borderTop: '2px solid',
                          borderColor: 'divider',
                          fontWeight: 600,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          Total ({displayAssets.length} assets)
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(
                            displayAssets.reduce((sum, asset) => sum + asset.depreciationAmount, 0)
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {(() => {
                            const totalDepreciation = displayAssets.reduce(
                              (sum, asset) => sum + asset.depreciationAmount,
                              0
                            );
                            const totalProjectDepreciation = displayAssets.reduce(
                              (sum, asset) =>
                                sum + asset.depreciationAmount * asset.projectAllocation * 0.01,
                              0
                            );
                            const percentage =
                              totalDepreciation > 0
                                ? (totalProjectDepreciation / totalDepreciation) * 100
                                : 0;
                            return `${percentage.toFixed(1)}%`;
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(
                            displayAssets.reduce(
                              (sum, asset) =>
                                sum + asset.depreciationAmount * asset.projectAllocation * 0.01,
                              0
                            )
                          )}
                        </Typography>
                      </TableCell>
                      {sortedActivities.map((activity) => {
                        const totalAmount = getActivityTotal(activity.id);
                        return (
                          <TableCell key={activity.id} sx={{ textAlign: 'center' }}>
                            {totalAmount > 0 ? (
                              <Typography variant="body2" fontWeight={600}>
                                {formatCurrency(totalAmount)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                -
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box
              sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {displayAssets.length} asset{displayAssets.length !== 1 ? 's' : ''}
                {showAllAssets && getAllocatedAssets().length > 0 && (
                  <> • {getAllocatedAssets().length} allocated</>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Amounts shown are for the project tax year
              </Typography>
            </Box>
          </>
        )}
      </PaperSection>
    </ContentWrapper>
  );
};

export default Depreciation;
